(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const currency = window.Shopify?.currency?.active || 'EUR';
  const locale = document.documentElement.lang || 'fr-FR';
  const money = (cents) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format((cents || 0) / 100);
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' }[character]));

  class CartDrawer {
    constructor(element) {
      this.element = element;
      this.panel = element.querySelector('.cart-drawer__panel');
      this.content = element.querySelector('[data-cart-content]');
      this.footer = element.querySelector('[data-cart-footer]');
      this.subtotal = element.querySelector('[data-cart-subtotal]');
      this.shipping = element.querySelector('[data-free-shipping]');
      this.note = element.querySelector('[data-cart-note]');
      this.openTriggers = [...document.querySelectorAll('[data-cart-open]')];
      this.bind();
      this.refresh(false);
    }

    bind() {
      this.openTriggers.forEach((button) => button.addEventListener('click', () => this.open()));
      this.element.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.content.addEventListener('click', (event) => this.handleClick(event));
      this.content.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input) return;
        const item = input.closest('[data-cart-item]');
        this.change(item?.dataset.key, Math.max(0, Number(input.value || 0)));
      });
      this.note?.addEventListener('change', () => this.updateNote(this.note.value));
      document.addEventListener('cart:refresh', (event) => this.refresh(Boolean(event.detail?.open)));
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !this.element.hidden) this.close(); });
    }

    open() {
      this.element.hidden = false;
      requestAnimationFrame(() => this.element.classList.add('is-open'));
      document.body.classList.add('is-locked');
      this.openTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      setTimeout(() => this.panel?.focus(), 30);
    }

    close() {
      this.element.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      this.openTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      setTimeout(() => { this.element.hidden = true; }, 220);
    }

    handleClick(event) {
      const item = event.target.closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-cart-quantity]');
      if (event.target.closest('[data-cart-remove]')) this.change(item.dataset.key, 0);
      if (event.target.closest('[data-cart-quantity-minus]')) this.change(item.dataset.key, Math.max(0, Number(input.value) - 1));
      if (event.target.closest('[data-cart-quantity-plus]')) this.change(item.dataset.key, Number(input.value) + 1);
    }

    async change(key, quantity) {
      if (!key) return;
      this.element.classList.add('is-loading');
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!response.ok) throw new Error('Mise à jour impossible.');
        this.render(await response.json());
      } catch (error) {
        window.LMDS?.showToast(error.message);
      } finally {
        this.element.classList.remove('is-loading');
      }
    }

    async updateNote(note) {
      try {
        await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note }) });
      } catch (_) { /* note non critique */ }
    }

    async refresh(openAfter = false) {
      try {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Panier indisponible.');
        this.render(await response.json());
        if (openAfter) this.open();
      } catch (error) {
        window.LMDS?.showToast(error.message);
      }
    }

    render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('is-empty', cart.item_count === 0);
      });
      if (this.subtotal) this.subtotal.textContent = money(cart.total_price);
      this.footer?.classList.toggle('is-hidden', cart.item_count === 0);
      this.updateShipping(cart.total_price);

      if (!cart.items.length) {
        this.content.innerHTML = `<div class="cart-empty"><span aria-hidden="true">◯</span><p>Votre panier est vide.</p><a class="button button--primary" href="${root}collections/all">DÉCOUVRIR LA GAMME</a></div>`;
        return;
      }

      this.content.innerHTML = `<div class="cart-items">${cart.items.map((item) => `
        <article class="cart-item" data-cart-item data-key="${escapeHtml(item.key)}">
          <a class="cart-item__media" href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(item.image)}${item.image.includes('?') ? '&' : '?'}width=240" width="120" height="120" loading="lazy" alt="${escapeHtml(item.product_title)}">` : ''}</a>
          <div class="cart-item__info">
            <a class="cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${item.variant_title ? `<small>${escapeHtml(item.variant_title)}</small>` : ''}
            <div class="cart-item__bottom">
              <div class="quantity-selector quantity-selector--small">
                <button type="button" data-cart-quantity-minus aria-label="Diminuer la quantité">−</button>
                <input type="number" value="${item.quantity}" min="0" data-cart-quantity aria-label="Quantité de ${escapeHtml(item.product_title)}">
                <button type="button" data-cart-quantity-plus aria-label="Augmenter la quantité">+</button>
              </div>
              <strong>${money(item.final_line_price)}</strong>
            </div>
          </div>
          <button class="cart-item__remove" type="button" data-cart-remove aria-label="Supprimer ${escapeHtml(item.product_title)}">×</button>
        </article>`).join('')}</div>`;
    }

    updateShipping(total = 0) {
      if (!this.shipping) return;
      const threshold = Number(this.shipping.dataset.threshold || 0);
      const text = this.shipping.querySelector('[data-free-shipping-text]');
      const progress = this.shipping.querySelector('[data-free-shipping-progress]');
      if (!threshold) { this.shipping.hidden = true; return; }
      this.shipping.hidden = false;
      const remaining = Math.max(0, threshold - total);
      const ratio = Math.min(1, total / threshold);
      if (text) text.textContent = remaining === 0
        ? this.shipping.dataset.reached
        : this.shipping.dataset.message.replace('[amount]', money(remaining));
      if (progress) progress.style.transform = `scaleX(${ratio})`;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const element = document.querySelector('[data-cart-drawer]');
    if (element) window.LMDSCart = new CartDrawer(element);
  });
})();
